import React, { useState } from "react";
import { motion } from "framer-motion";

const states = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
    "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
    "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
    "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
    "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

export default function VoterGuide() {
    // Generate year dropdown (from 1900 to current year minus 17)
    const minYear = 1900;
    const maxYear = new Date().getFullYear() - 17;
    const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        dob: "",
        state: "",
        city: "",
        address: "",
        zipcode: "",
        email: "",
        telephone: "",
        reminder: false,
        sms: false,
    });

    const [resultMessage, setResultMessage] = useState<string | null>(null);

    const isValid =
        form.firstName &&
        form.lastName &&
        form.dob &&
        form.state &&
        form.city &&
        form.address &&
        form.zipcode &&
        form.email &&
        form.reminder;

    const apiUrl = process.env.REACT_APP_API_URL;


    // Handler
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        if (type === "checkbox") {
            setForm((f) => ({
                ...f,
                [name]: (e.target as HTMLInputElement).checked,
            }));
        } else {
            setForm((f) => ({
                ...f,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isValid) return;

        if (form.reminder) {
            try {
                const response = await fetch(`${apiUrl}/reminder-subscription`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        firstName: form.firstName,
                        lastName: form.lastName,
                        email: form.email,
                        state: form.state,
                        reminder: form.reminder,
                    }),
                });
                if (response.ok) {
                    setResultMessage("Thanks! You are subscribed for reminders and your voter guide is on its way.");
                } else {
                    setResultMessage("Submission failed. Please try again.");
                }
            } catch (err) {
                setResultMessage("Something went wrong. Please try again.");
            }
        } else {
            setResultMessage("Thanks! Your voter guide is on its way.");
        }
        // Optionally clear form here
        setTimeout(() => setResultMessage(null), 6000);
    };

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const days = Array.from({ length: 31 }, (_, i) => i + 1);

    return (
        <section className="min-h-screen flex items-center justify-center bg-black text-white">
            <div className="bg-neutral-900 rounded-2xl shadow-2xl p-8 md:p-12 w-full max-w-xl relative border-2 border-white">
                <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-white">Get Your Voter Guide</h2>
                <form className="space-y-6" onSubmit={handleSubmit}>
                    {/* Name fields */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="font-semibold text-white">First Name <span className="text-red-600">*</span></label>
                            <input
                                type="text"
                                name="firstName"
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                value={form.firstName}
                                onChange={handleChange}
                                placeholder="First Name"
                            />
                        </div>
                        <div>
                            <label className="font-semibold text-white">Last Name <span className="text-red-600">*</span></label>
                            <input
                                type="text"
                                name="lastName"
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                value={form.lastName}
                                onChange={handleChange}
                                placeholder="Last Name"
                            />
                        </div>
                          <div>
                            <label className="font-semibold text-white">
                                State <span className="text-red-600">*</span>
                            </label>
                            <select
                                name="state"
                                value={form.state}
                                onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                required
                            >
                                <option value="">Select State</option>
                                {states.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Address */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      
                         <div>
                            <label className="font-semibold text-white">City <span className="text-red-600">*</span></label>
                            <input type="text" name="city" value={form.city} onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                placeholder="City"
                            />
                        </div>

                        <div>
                            <label className="font-semibold text-white">Home Address <span className="text-red-600">*</span></label>
                            <input type="text" name="address" value={form.address} onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                placeholder="Address"
                            />
                        </div>
                       
                        <div>
                            <label className="font-semibold text-white">Zipcode <span className="text-red-600">*</span></label>
                            <input type="text" name="zipcode" value={form.zipcode} onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                placeholder="Zipcode"
                            />
                        </div>
                    </div>

                    {/* DOB, Email, Telephone */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="font-semibold text-white">Date of Birth <span className="text-red-600">*</span></label>
                            <input
                                type="date"
                                name="dob"
                                value={form.dob}
                                onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                placeholder="Date of Birth"
                            />
                        </div>
                        <div>
                            <label className="font-semibold text-white">Email <span className="text-red-600">*</span></label>
                            <input type="email" name="email" value={form.email} onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                placeholder="Email Address"
                            />
                        </div>
                        <div>
                            <label className="font-semibold text-white">Telephone</label>
                            <input type="text" name="telephone" value={form.telephone} onChange={handleChange}
                                className="w-full bg-black text-white border border-gray-400 rounded px-3 py-2 mt-1 focus:border-red-600"
                                placeholder="Phone (Optional)"
                            />
                        </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="flex flex-col space-y-2">
                        <label className="flex items-center text-white">
                            <input type="checkbox" name="reminder" checked={form.reminder} onChange={handleChange} className="mr-2 accent-red-600" />
                            <span className="font-semibold">Receive election reminders and action alerts updates from 123-Vote. <span className="text-red-600">*</span></span>
                        </label>
                        <label className="flex items-center text-white">
                            <input type="checkbox" name="sms" checked={form.sms} onChange={handleChange} className="mr-2 accent-red-600" />
                            Receive text messages from 123-Vote.
                        </label>
                    </div>
                    {/* Submit Button */}
                    <motion.button
                        whileHover={isValid ? { scale: 1.04, backgroundColor: "#d90429" } : {}}
                        type="submit"
                        disabled={!isValid}
                        className={`w-full py-3 rounded-full font-bold text-lg shadow-md transition-all duration-200 ${isValid ? "bg-red-600 text-white cursor-pointer hover:bg-red-700" : "bg-gray-400 text-white cursor-not-allowed"
                            }`}
                    >
                        Get My Voter Guide
                    </motion.button>
                </form>
            </div>
        </section>
    );
}


